import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { Badge, Box, IconButton, InputAdornment, TextField, Tooltip } from '@mui/material';
import * as React from 'react';

/** Height of the search row, so the drawer's scroll area can leave room for it. */
export const searchRowHeight = 56;

export const SEARCH_LABEL = 'Search properties and units';

interface DrawerSearchProps {
  /** Whether the drawer is expanded. When collapsed only a search icon is shown. */
  open: boolean;
  /** Expand the drawer. Called from the collapsed icon so the field can take focus. */
  onOpen: () => void;
  query: string;
  onQueryChange: (query: string) => void;
  /** Enter in the field. */
  onSubmit: () => void;
}

export const DrawerSearch = ({
  open,
  onOpen,
  query,
  onQueryChange,
  onSubmit,
}: DrawerSearchProps) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [focusWhenOpen, setFocusWhenOpen] = React.useState(false);
  const hasQuery = query.trim() !== '';

  // The field is not rendered while the drawer is collapsed, so focus has to wait
  // for the expanded render that follows the icon click.
  React.useEffect(() => {
    if (open && focusWhenOpen) {
      inputRef.current?.focus();
      setFocusWhenOpen(false);
    }
  }, [open, focusWhenOpen]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      onSubmit();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      onQueryChange('');
    }
  };

  if (!open) {
    return (
      <Box
        sx={{
          height: searchRowHeight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Tooltip
          title={hasQuery ? 'Search (filter active)' : 'Search'}
          placement="right"
          describeChild
        >
          <IconButton
            aria-label={hasQuery ? `${SEARCH_LABEL}, filter active` : SEARCH_LABEL}
            onClick={() => {
              setFocusWhenOpen(true);
              onOpen();
            }}
            sx={{ color: 'white' }}
          >
            {/* The list stays filtered while the drawer is collapsed, so the icon has to say so. */}
            <Badge variant="dot" color="primary" overlap="circular" invisible={!hasQuery}>
              <SearchIcon />
            </Badge>
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box sx={{ height: searchRowHeight, display: 'flex', alignItems: 'center', px: 1 }}>
      <TextField
        fullWidth
        size="small"
        placeholder={SEARCH_LABEL}
        value={query}
        onChange={(event) => onQueryChange(event.target.value)}
        onKeyDown={handleKeyDown}
        inputRef={inputRef}
        inputProps={{ 'aria-label': SEARCH_LABEL, autoComplete: 'off' }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
            </InputAdornment>
          ),
          endAdornment: query ? (
            <InputAdornment position="end">
              <IconButton
                aria-label="Clear search"
                size="small"
                edge="end"
                onClick={() => {
                  onQueryChange('');
                  inputRef.current?.focus();
                }}
                sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            color: 'white',
            '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.4)' },
            '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.7)' },
            '&.Mui-focused fieldset': { borderColor: 'white' },
          },
          '& .MuiInputBase-input::placeholder': {
            color: 'rgba(255, 255, 255, 0.7)',
            opacity: 1,
          },
        }}
      />
    </Box>
  );
};
